import { Observable } from "rxjs/Observable";
import { forkJoin } from "rxjs/observable/forkJoin";
import { ofType } from "redux-observable";
import { flatMap, catchError } from "rxjs/operators";
import { combineEpics } from "redux-observable";

import App from "../../lib/app";
import * as AccActions from "../actions/acc";
import LoginActions from "../reducers/login.reducer";

// Epic para obtener parámetros ACC
export const getAccParameters = (action$) =>
  action$.pipe(
    ofType("START_GET_ACC_PARAMETERS"),
    flatMap((action) => {
      const headers = {
        "x-api-key": App.config.apis.acc.apiKey,
        apiKey: App.config.apis.acc.apiKey,
      };
      return Observable.from(App.api.acc.getAccParameters({ headers })).pipe(
        flatMap((response) => {
          // Procesar la respuesta del backend que llega como: { success: true, data: { meta: {...}, data: [...] } }
          let processedData;

          if (response?.success && response?.data) {
            processedData = {
              meta: response.data.meta || {},
              data: response.data.data || [],
            };
          } else if (
            response?.data?.data &&
            Array.isArray(response.data.data)
          ) {
            processedData = {
              meta: response.data.meta || {},
              data: response.data.data,
            };
          } else if (response?.data && Array.isArray(response.data)) {
            processedData = {
              meta: {},
              data: response.data,
            };
          } else {
            processedData = {
              meta: response?.meta || {},
              data: [],
            };
          }

          return Observable.of(
            AccActions.getAccParametersSuccess(processedData)
          );
        }),
        catchError((e) => {
          if (e.statusCode === 401) {
            return Observable.of(LoginActions.submitLogout());
          } else {
            return Observable.of(AccActions.getAccParametersFail(e));
          }
        })
      );
    })
  );

// Epic para obtener tasas de usura
export const getUsuraRates = (action$) =>
  action$.pipe(
    ofType("START_GET_USURA_RATES"),
    flatMap((action) => {
      const headers = {
        "x-api-key": App.config.apis.acc.apiKey,
        apiKey: App.config.apis.acc.apiKey,
      };
      const url = action.filters || {};
      return Observable.from(App.api.acc.getUsuraRates({ url, headers })).pipe(
        flatMap((response) => {
          // Procesar la respuesta del backend que llega como: { success: true, data: { meta: {...}, data: [...] } }
          let processedData;

          if (response?.success && response?.data) {
            processedData = {
              meta: response.data.meta || {},
              data: response.data.data || [],
            };
          } else if (
            response?.data?.data &&
            Array.isArray(response.data.data)
          ) {
            processedData = {
              meta: response.data.meta || {},
              data: response.data.data,
            };
          } else if (response?.data && Array.isArray(response.data)) {
            processedData = {
              meta: {},
              data: response.data,
            };
          } else {
            processedData = {
              meta: response?.meta || {},
              data: [],
            };
          }

          return Observable.of(AccActions.getUsuraRatesSuccess(processedData));
        }),
        catchError((e) => {
          if (e.statusCode === 401) {
            return Observable.of(LoginActions.submitLogout());
          } else {
            return Observable.of(AccActions.getUsuraRatesFail(e));
          }
        })
      );
    })
  );

// Epic para crear o actualizar tasas de usura
export const createOrUpdateUsuraRate = (action$) =>
  action$.pipe(
    ofType("START_CREATE_OR_UPDATE_USURA_RATE"),
    flatMap((action) => {
      const headers = {
        "x-api-key": App.config.apis.acc.apiKey,
        apiKey: App.config.apis.acc.apiKey,
      };

      // Determinar si es creación o actualización basándose en si existe ID
      const operationType = action.data.id ? "update" : "create";

      return Observable.from(
        App.api.acc.createOrUpdateUsuraRate({
          body: action.data,
          headers,
        })
      ).pipe(
        flatMap((response) => {
          return Observable.of(
            AccActions.createOrUpdateUsuraRateSuccess(response, operationType)
          );
        }),
        catchError((e) => {
          if (e.statusCode === 401) {
            return Observable.of(LoginActions.submitLogout());
          } else {
            return Observable.of(AccActions.createOrUpdateUsuraRateFail(e));
          }
        })
      );
    })
  );

// Epic combinado para obtener todos los datos ACC necesarios para la página
export const getAccShowcase = (action$) =>
  action$.pipe(
    ofType("START_ACC_SHOWCASE"),
    flatMap((action) => {
      const headers = {
        "x-api-key": App.config.apis.acc.apiKey,
        apiKey: App.config.apis.acc.apiKey,
      };

      // Primero disparamos las acciones de loading
      return Observable.concat(
        Observable.of(AccActions.startGetAccParameters()),
        Observable.of(AccActions.startGetUsuraRates()),
        forkJoin([
          Observable.from(App.api.acc.getAccParameters({ headers })),
          Observable.from(App.api.acc.getUsuraRates({ headers })),
        ]).pipe(
          flatMap((responses) => {
            const [parametersResponse, usuraRatesResponse] = responses;

            // Procesar respuesta de parámetros
            let parametersData;
            if (parametersResponse?.success && parametersResponse?.data) {
              parametersData = {
                meta: parametersResponse.data.meta || {},
                data: parametersResponse.data.data || [],
              };
            } else {
              parametersData = {
                meta: parametersResponse?.meta || {},
                data: [],
              };
            }

            // Procesar respuesta de tasas de usura
            let usuraRatesData;
            if (usuraRatesResponse?.success && usuraRatesResponse?.data) {
              usuraRatesData = {
                meta: usuraRatesResponse.data.meta || {},
                data: usuraRatesResponse.data.data || [],
              };
            } else {
              usuraRatesData = {
                meta: usuraRatesResponse?.meta || {},
                data: [],
              };
            }

            return Observable.concat(
              Observable.of(AccActions.getAccParametersSuccess(parametersData)),
              Observable.of(AccActions.getUsuraRatesSuccess(usuraRatesData))
            );
          }),
          catchError((e) => {
            if (e.statusCode === 401) {
              return Observable.of(LoginActions.submitLogout());
            } else {
              return Observable.concat(
                Observable.of(AccActions.getAccParametersFail(e)),
                Observable.of(AccActions.getUsuraRatesFail(e))
              );
            }
          })
        )
      );
    })
  );

export default combineEpics(
  getAccParameters,
  getUsuraRates,
  createOrUpdateUsuraRate,
  getAccShowcase
);
